import type { SpanExporter, ReadableSpan } from '@opentelemetry/sdk-trace-base'
import { ExportResultCode, type ExportResult } from '@opentelemetry/core'
import { JsonTraceSerializer } from '@opentelemetry/otlp-transformer'
import { tokenManager } from '../api/instance'

/**
 * Custom OTLP exporter that authenticates with the app's JWT token.
 * Uses fetch() directly (not Axios) to avoid being instrumented by the
 * XHR/fetch auto-instrumentation, which would cause infinite export loops.
 */
export class AuthenticatedOtlpExporter implements SpanExporter {
  private _shutdownOnce = false

  export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    const body = JsonTraceSerializer.serializeRequest(spans)
    if (!body) {
      resultCallback({ code: ExportResultCode.SUCCESS })
      return
    }

    const token = tokenManager.getAccessToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Use fetch() directly — NOT Axios — so the fetch instrumentation's ignoreUrls
    // can filter this out, and we avoid Axios interceptors entirely.
    // Decode the Uint8Array to string for fetch body.
    const bodyStr = new TextDecoder().decode(body)

    fetch('/api/ingest/otlp/v1/traces', {
      method: 'POST',
      headers,
      body: bodyStr,
      keepalive: true,
    })
      .then(res => {
        resultCallback({
          code: res.ok ? ExportResultCode.SUCCESS : ExportResultCode.FAILED,
        })
      })
      .catch(() => {
        resultCallback({ code: ExportResultCode.FAILED })
      })
  }

  shutdown(): Promise<void> {
    if (this._shutdownOnce) return Promise.resolve()
    this._shutdownOnce = true
    return Promise.resolve()
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
}
